import { Policy, PolicyCreateEntry, PolicyUpdateEntry, PolicyConsent, PolicyConsentCreateEntry, PolicyQuestion, PolicyQuestionCreateEntry, PolicyQuestionUpdateEntry } from 'public-contracts';
import { configs } from '../configs';
import authProvider from '../authProvider';

// Wrapper function to handle 401 errors with token refresh
const withTokenRefresh = async <T>(apiCall: () => Promise<T>): Promise<T> => {
  try {
    return await apiCall();
  } catch (error: any) {
    // Check if it's a 401 error
    if (error?.status === 401 || error?.message?.includes('401')) {
      console.log(
        '401 error detected in policyService, attempting token refresh...'
      );

      try {
        // Attempt to refresh the token
        await authProvider.auth.fetchRefreshToken();
        console.log('Token refresh successful, retrying API call...');

        // Retry the original API call
        return await apiCall();
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // If refresh fails, throw the original error
        throw error;
      }
    }

    // If it's not a 401 error, throw the original error
    throw error;
  }
};

const getAuthHeader = async (): Promise<Record<string, string>> => {
  const accessToken = await authProvider.auth.getAccessToken();
  if (!accessToken) {
    return {};
  }
  return {
    Authorization: `Bearer ${accessToken}`,
  };
};

export class PolicyService {
  private apiRoot = configs.apiRoot;

  async getPolicies(params?: {
    size?: number;
    page?: number;
    filter?: any;
  }): Promise<Policy[]> {
    return withTokenRefresh(async () => {
      const queryParams = new URLSearchParams();
      const page = params?.page ?? 1;
      const size = params?.size ?? 1000;
      
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());
      
      if (params?.filter) {
        queryParams.append('filter', JSON.stringify(params.filter));
      }
      
      const url = `${this.apiRoot}/policies${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const authHeaders = await getAuthHeader();
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to fetch policies: ${res.status} ${errorText}`);
      }
      
      const result = await res.json();
      return Array.isArray(result) ? result : result.data || [];
    });
  }

  async getPolicy(id: string): Promise<Policy> {
    return withTokenRefresh(async () => {
      const url = `${this.apiRoot}/policies/${id}`;
      const authHeaders = await getAuthHeader();
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to fetch policy: ${res.status} ${errorText}`);
      }
      
      return await res.json();
    });
  }

  async createPolicy(data: PolicyCreateEntry): Promise<Policy> {
    return withTokenRefresh(async () => {
      const url = `${this.apiRoot}/policies`;
      const authHeaders = await getAuthHeader();
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to create policy: ${res.status} ${errorText}`);
      }
      
      return await res.json();
    });
  }

  async updatePolicy(id: string, data: PolicyUpdateEntry): Promise<Policy> {
    return withTokenRefresh(async () => {
      const url = `${this.apiRoot}/policies/${id}`;
      const authHeaders = await getAuthHeader();
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...data, id }),
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to update policy: ${res.status} ${errorText}`);
      }
      
      return await res.json();
    });
  }

  async deletePolicy(id: string): Promise<void> {
    return withTokenRefresh(async () => {
      const url = `${this.apiRoot}/policies/${id}`;
      const authHeaders = await getAuthHeader();
      const res = await fetch(url, {
        method: 'DELETE',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to delete policy: ${res.status} ${errorText}`);
      }
    });
  }

  // Policy Consent methods
  async getPolicyConsent(policyId: string, userId: string): Promise<PolicyConsent | null> {
    return withTokenRefresh(async () => {
      const url = `${this.apiRoot}/policy-consents?policyId=${policyId}&userId=${userId}`;
      const authHeaders = await getAuthHeader();
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
      });
      
      if (!res.ok) {
        if (res.status === 404) {
          return null;
        }
        const errorText = await res.text();
        throw new Error(`Failed to fetch policy consent: ${res.status} ${errorText}`);
      }
      
      const result = await res.json();
      return Array.isArray(result) ? (result[0] || null) : result;
    });
  }

  async consentToPolicy(data: PolicyConsentCreateEntry): Promise<PolicyConsent> {
    return withTokenRefresh(async () => {
      const url = `${this.apiRoot}/policy-consents`;
      const authHeaders = await getAuthHeader();
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          consentedAt: data.consentedAt || new Date().toISOString(),
        }),
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to consent to policy: ${res.status} ${errorText}`);
      }
      
      return await res.json();
    });
  }

  // Policy Question methods
  async getPolicyQuestions(policyId?: string, userId?: string): Promise<PolicyQuestion[]> {
    return withTokenRefresh(async () => {
      const queryParams = new URLSearchParams();
      if (policyId) queryParams.append('policyId', policyId);
      if (userId) queryParams.append('userId', userId);
      
      const url = `${this.apiRoot}/policy-questions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const authHeaders = await getAuthHeader();
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to fetch policy questions: ${res.status} ${errorText}`);
      }
      
      const result = await res.json();
      return Array.isArray(result) ? result : result.data || [];
    });
  }

  async askQuestion(data: PolicyQuestionCreateEntry): Promise<PolicyQuestion> {
    return withTokenRefresh(async () => {
      const url = `${this.apiRoot}/policy-questions`;
      const authHeaders = await getAuthHeader();
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          askedAt: data.askedAt || new Date().toISOString(),
          isResolved: data.isResolved || false,
          questionType: data.questionType || 'MANAGER',
          answeredByAI: data.answeredByAI || false,
        }),
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to ask question: ${res.status} ${errorText}`);
      }
      
      return await res.json();
    });
  }

  async answerQuestion(data: PolicyQuestionUpdateEntry): Promise<PolicyQuestion> {
    return withTokenRefresh(async () => {
      const url = `${this.apiRoot}/policy-questions/${data.id}`;
      const authHeaders = await getAuthHeader();
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          answeredAt: data.answeredAt || new Date().toISOString(),
        }),
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to answer question: ${res.status} ${errorText}`);
      }
      
      return await res.json();
    });
  }
}

export const policyService = new PolicyService();

